/**
 * WordPress REST API client for use when admin-panel runs inside a WP iframe.
 *
 * All requests attach the X-VFR-Session header obtained via WordPressAuthBridge.
 * On 401, requests a token refresh from the parent frame via postMessage.
 */

function getSession() {
  return sessionStorage.getItem('vfr-wp-rest-session') || null;
}

function getWordPressUrl() {
  return sessionStorage.getItem('vfr-wordpress-url') || null;
}

function requestTokenRefresh() {
  if (window.parent && window.self !== window.top) {
    window.parent.postMessage({ type: 'vfr-request-auth-token' }, '*');
  }
}

async function wpFetch(path, options = {}) {
  const wpUrl = getWordPressUrl();
  if (!wpUrl) throw new Error('WordPress URL not available');

  const session = getSession();
  const headers = {
    'Content-Type': 'application/json',
    ...(session ? { 'X-VFR-Session': session } : {}),
    ...options.headers,
  };

  const url = `${wpUrl}/wp-json${path}`;
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 401) {
    requestTokenRefresh();
    // Wait briefly for token refresh, then retry once
    await new Promise((r) => setTimeout(r, 2000));
    const retrySession = getSession();
    if (retrySession && retrySession !== session) {
      const retryHeaders = { ...headers, 'X-VFR-Session': retrySession };
      const retry = await fetch(url, { ...options, headers: retryHeaders, credentials: 'include' });
      if (!retry.ok) throw new Error(`WP REST ${retry.status}: ${await retry.text()}`);
      return retry.json();
    }
    throw new Error('WordPress session expired');
  }

  if (!response.ok) {
    const text = await response.text();
    let msg;
    try { msg = JSON.parse(text)?.message || text; } catch { msg = text; }
    throw new Error(msg);
  }

  return response.json();
}

export const adminApi = {
  getStatus() {
    return wpFetch('/vfr/v1/admin/status');
  },

  activateLicense(licenseKey) {
    return wpFetch('/vfr/v1/admin/activate-license', {
      method: 'POST',
      body: JSON.stringify({ license_key: licenseKey }),
    });
  },

  deactivateLicense() {
    return wpFetch('/vfr/v1/admin/deactivate-license', { method: 'POST' });
  },

  completeSetup() {
    return wpFetch('/vfr/v1/admin/complete-setup', { method: 'POST' });
  },

  checkUpdates() {
    return wpFetch('/vfr/v1/admin/check-updates', { method: 'POST' });
  },

  getReports(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    const qs = params.toString();
    return wpFetch(`/vfr/v1/admin/reports${qs ? `?${qs}` : ''}`);
  },
};

export const productsApi = {
  list(params = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.per_page) qs.set('per_page', params.per_page);
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    const q = qs.toString();
    return wpFetch(`/virtual-fitting-room/v1/products${q ? `?${q}` : ''}`);
  },

  toggle(productId, enabled) {
    return wpFetch('/virtual-fitting-room/v1/products/toggle', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, enabled }),
    });
  },

  bulk(action, productIds) {
    return wpFetch('/virtual-fitting-room/v1/products/bulk', {
      method: 'POST',
      body: JSON.stringify({ action, product_ids: productIds, enabled: action === 'enable' }),
    });
  },

  generatePreview(productId) {
    return wpFetch('/virtual-fitting-room/v1/saas/generate-model', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, model_type: 'slim-female' }),
    });
  },
};
